import qs from 'qs';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import type { InjectedRouter } from 'react-router';

import { useAddToast } from '../../../contexts/ToastContext';
import { testPasswordStrength } from '../../../helpers/passwordStrength';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { hardRedirectTo } from '../../../utils';
import useInviteToken from '../../auth/context/useInviteToken';
import PasswordValidationTooltip from '../../auth/form/PasswordValidationTooltip';
import AuthFormTermsLink from '../../auth/form/TermsLink';
import Button from '../../generic/Button';
import LabeledPasswordInput from '../../generic/Forms/LabeledPasswordInput';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import { icons } from '../../generic/Icon';
import { RouterLink } from '../../generic/Link';
import PasswordMeter from '../../generic/PasswordMeter';
import SocialLoginButtons from '../components/SocialLoginButtons';
import { presentRequestError } from '../errorPresentation';
import useAuthState from '../hooks/useAuthState';
import { useRegister } from '../hooks/useRegister';
import useResolveUser from '../hooks/useResolveUser';
import AuthLayoutTwoCol from '../layout/AuthLayoutTwoCol';
import ConfirmPasswordStep from '../steps/ConfirmPasswordStep';
import MfaStep from '../steps/MfaStep';
import OrgPickerStep from '../steps/OrgPickerStep';
import VerifyEmailStep from '../steps/VerifyEmailStep';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { VerifyEmailSignupResponse } from '../../../../shared/types/routes/auth/emailVerification';
import type { RouterProps } from '../../../types';

import styles from '../Login.module.less';
import registerStyles from './Register.module.less';

export type RegisterPageProps = {
  location: RouterProps['location'];
  /** react-router v3 injects this; only `push` is used, for client-side navigation to the login page. */
  router: Pick<InjectedRouter, 'push'>;
};

// When the entered email already belongs to one or more orgs (or a domain-claim org), the picker offers those plus
// "create a new organization"; a create-new then either confirms an existing password or sets a new one. Confirming
// a password whose account has MFA configured holds at the same challenge step the login flow uses.
type RegisterStep = 'email' | 'org-picker' | 'confirm-password' | 'password' | 'verify' | 'mfa';

// Same validator the resolve-user signup schema uses on the backend, so the client rejects a malformed address
// before the round-trip rather than letting the server be the first to say no.
const emailSchema = z.email();

// Wait for a typing pause before flagging a confirm-password mismatch, so the warning doesn't flash on every
// keystroke while the confirmation is still being typed.
const PASSWORD_MATCH_DEBOUNCE_MS = 400;

const Register = ({ location, router }: RegisterPageProps) => {
  const { inviteToken, next } = location.query;

  const [inviteAlert, setInviteAlert] = useState<string | null>(null);
  // `setInviteAlert` is a stable setter, safe as the hook's effect dependency (it refetches only when the token
  // changes). It resolves the invite to the org name and the invited address.
  const { orgTitle, invitedEmail } = useInviteToken(setInviteAlert, inviteToken);

  const [step, setStep] = useState<RegisterStep>('email');
  const [email, setEmail] = useState(location.query.email ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [mfaMethod, setMfaMethod] = useState<MfaMethodType | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  // True while a register submission's outcome is still awaited. Backing out of the flow clears it, so a
  // late-resolving request can no longer yank the user into onboarding for an account they walked away
  // from; it also makes the settle effect one-shot per submission.
  const isRegisterAwaitedRef = useRef(false);

  const { data: authState, isError: isAuthStateError, refetch: refetchAuthState } = useAuthState();
  const oauthProviders = authState?.kind === 'idle' ? authState.oauthProviders : [];
  const isSignupDisabled = authState?.kind === 'idle' ? authState.signupDisabled : false;

  const resolveUser = useResolveUser();
  const register = useRegister();
  const addToast = useAddToast();
  const { debouncedValue: debouncedPassword } = useDebouncedValue(password, PASSWORD_MATCH_DEBOUNCE_MS);
  const { debouncedValue: debouncedConfirmPassword } = useDebouncedValue(confirmPassword, PASSWORD_MATCH_DEBOUNCE_MS);

  const isEmailLocked = Boolean(invitedEmail);
  // An invite token that failed to resolve (expired/invalid): the alert is shown, but block progression so the
  // user can't retype a different email and dead-end at the same error on submit.
  const isInviteInvalid = Boolean(inviteToken && inviteAlert && !invitedEmail);
  // The invite is still resolving: neither the invited email (success) nor an alert (failure) has landed.
  // Submitting during this window would let a hand-typed email race the invite result, which then
  // overwrites the field mid-flow.
  const isInviteResolving = Boolean(inviteToken) && !invitedEmail && !inviteAlert;
  const registerWithoutInviteHref = next ? `/auth/register?${qs.stringify({ next })}` : '/auth/register';
  const passwordStrength = testPasswordStrength(password);
  const isPasswordValid =
    passwordStrength.isLongEnough
    && passwordStrength.hasLowerCase
    && passwordStrength.hasUpperCase
    && passwordStrength.hasNumber
    && passwordStrength.hasSymbol;
  const arePasswordsEqual = password === confirmPassword;
  const canSubmit = isPasswordValid && arePasswordsEqual;
  // Debounce the *appearance* of the mismatch warning: surface it once typing settles in *either* field on an
  // unequal pair (debouncing both fields, not just the confirmation, keeps it from flashing on every keystroke
  // when the original password is edited afterward), but let the live comparison clear it the moment they match.
  const isPasswordMismatch =
    confirmPassword.length > 0
    && !arePasswordsEqual
    && debouncedPassword === password
    && debouncedConfirmPassword === confirmPassword;

  const heading = orgTitle ? `Sign up and join ${orgTitle}` : 'Sign up';
  const loginQuery = qs.stringify({ email: email || undefined, next }, { skipNulls: true });
  const loginHref = loginQuery ? `/auth/login?${loginQuery}` : '/auth/login';

  // Once the invite metadata lands, prefill and lock the email to the invited address.
  useEffect(() => {
    if (invitedEmail) {
      setEmail(invitedEmail);
    }
  }, [invitedEmail]);

  // The sign-up-without-an-invite escape hatch navigates within this mounted page; the hook does nothing
  // once the token is gone, so a stale invite alert would otherwise keep rendering.
  useEffect(() => {
    if (!inviteToken) {
      setInviteAlert(null);
    }
  }, [inviteToken]);

  // Focus the password field when the flow advances to the set-password step.
  useEffect(() => {
    if (step === 'password') {
      passwordRef.current?.focus();
    }
  }, [step]);

  // Advance from the settled resolve result rather than the mutate callback: navigating from the callback updates
  // this step while React Query is still notifying subscribers (an unmounted-update warning under React 17).
  useEffect(() => {
    if (!resolveUser.data) {
      return;
    }
    const { candidates, signupSuggestions, hasOrphanAccount } = resolveUser.data;
    if (hasOrphanAccount) {
      // The email belongs to an account that never finished onboarding. Don't create a duplicate — send them to
      // login (email prefilled) to authenticate org-less and resume onboarding. Source the address from the
      // resolved request, not the live `email` state, so this effect stays keyed to the resolve, not to typing.
      const query = qs.stringify(
        { email: resolveUser.variables?.identifier || undefined, next },
        { skipNulls: true },
      );
      router.push(query ? `/auth/login?${query}` : '/auth/login');
    } else if (candidates.length > 0 || signupSuggestions.length > 0) {
      // The email already has member orgs, or its domain is claimed by one: let the user sign into an existing org
      // or create a new one.
      setStep('org-picker');
    } else {
      setStep('password');
    }
  }, [resolveUser.data, resolveUser.variables, next, router]);

  // Route on the settled register result, for the same reason. Confirming a password whose account has MFA holds
  // at the challenge step; an invite joined an existing org (→ dashboard); a pending email verification holds at
  // the verify step; a Cloud direct registration with an auto-created org lands on project import; an org-less
  // registration (on-prem, or a create-new that names its org) goes to onboarding.
  useEffect(() => {
    if (!register.data || !isRegisterAwaitedRef.current) {
      return;
    }
    isRegisterAwaitedRef.current = false;
    if (register.data.status === 'mfa_required') {
      setMfaMethod(register.data.mfaMethod);
      setStep('mfa');
      return;
    }
    if (register.data.joinedExistingOrganization) {
      // Honor the sanitized server-provided redirect (`?next=`), falling back to the dashboard.
      hardRedirectTo(register.data.to || '/');
    } else if (register.data.emailVerificationRequired) {
      setStep('verify');
    } else if (register.data.organizationId) {
      // Cloud auto-created an org; honor an explicit `next` deep-link, else land on first-run project import.
      hardRedirectTo(next && register.data.to ? register.data.to : '/projects/import');
    } else {
      hardRedirectTo('/onboarding');
    }
  }, [register.data, next]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError(null);
  };

  const handleEmailSubmit = () => {
    // Guard the same conditions that disable the Continue button: the form's onSubmit (Enter) calls this
    // directly, so an invalid invite or a not-yet-resolved auth state must block here too — otherwise Enter
    // could slip past the disabled button (and past the signup-disabled gate while it is still loading).
    if (resolveUser.isPending || isInviteInvalid || isInviteResolving || !authState) {
      return;
    }
    if (!emailSchema.safeParse(email).success) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);
    if (isEmailLocked) {
      // Invited: the address is fixed to the inviting org, so skip the member-org lookup and collect a password.
      setStep('password');
      return;
    }
    resolveUser.mutate({ identifier: email, context: 'signup' });
  };

  // Return to the email step to sign up under a different address, clearing the password entries so they aren't
  // carried over to another account.
  const handleReset = () => {
    isRegisterAwaitedRef.current = false;
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setStep('email');
  };

  // Chosen "create a new organization" from the picker: a returning email with a password confirms it inline; an
  // all-SSO email (or a brand-new email reached via a domain-claim suggestion) sets a new password instead.
  const handleCreateNew = () => {
    setError(null);
    // A confirmable password account (member OR org-less) → confirm it; otherwise set a new password (an all-SSO
    // email verifies by code; a social orphan is already verified). `hasPasswordAccount` spans org-less rows, which
    // the member-only `candidates` don't.
    setStep(resolveUser.data?.hasPasswordAccount ? 'confirm-password' : 'password');
  };

  const submitRegister = (submittedPassword: string) => {
    if (register.isPending) {
      return;
    }
    setError(null);
    isRegisterAwaitedRef.current = true;
    register.mutate(
      { email, password: submittedPassword, ...(inviteToken ? { inviteToken } : {}), ...(next ? { next } : {}) },
      {
        onError: requestError => {
          const presented = presentRequestError(requestError, {
            inlineFallback: 'Could not create your account. Please try again.',
            toastFallback: 'Something went wrong. Please try again.',
          });
          if (presented.kind === 'inline') {
            setError(presented.message);
            return;
          }
          addToast({ type: 'error', content: presented.message });
        },
      },
    );
  };

  const handleRegister = () => {
    if (!canSubmit) {
      return;
    }
    submitRegister(password);
  };

  // Where a verified Cloud registration lands, from the confirm response (the account and its auto-created org
  // only exist once the code is confirmed): an org-less account onboards; otherwise honor an explicit `?next=`
  // deep-link (the server echoes the sanitized target as `to`), falling back to first-run project import.
  const resolveVerifiedTarget = (result?: VerifyEmailSignupResponse) => {
    if (!result?.organizationId) {
      return '/onboarding';
    }
    return next && result.to ? result.to : '/projects/import';
  };

  const renderSignupDisabled = () => (
    <form className={styles.card} onSubmit={event => event.preventDefault()}>
      <h1 className={styles.heading}>Sign up</h1>
      <p role="alert" className={styles.error}>
        User registration has been disabled. Please contact your FOSSA administrator for an invite.
      </p>
      <div className={registerStyles.altAction}>
        Already have an account? <RouterLink to={loginHref}>Sign in</RouterLink>
      </div>
    </form>
  );

  const renderEmailStep = () => (
    <form
      className={styles.card}
      onSubmit={event => {
        event.preventDefault();
        handleEmailSubmit();
      }}
    >
      <h1 className={styles.heading}>{heading}</h1>
      {inviteAlert && (
        <p role="alert" className={styles.error}>
          {inviteAlert}
        </p>
      )}
      <div className={styles.fieldGroup}>
        <LabeledTextInput
          label="Email"
          type="email"
          name="email"
          placeholder="Enter your email"
          value={email}
          autoFocus
          isRequired
          isReadOnly={isEmailLocked}
          onChange={handleEmailChange}
        />
      </div>
      {emailError && (
        <p role="alert" className={styles.error}>
          {emailError}
        </p>
      )}
      {/* A settled auth-state failure would otherwise present as a forever-disabled Continue with no way out. */}
      {isAuthStateError && (
        <>
          <p role="alert" className={styles.error}>
            Could not load sign-up options. Please try again.
          </p>
          <Button type="anchor" size="small" onClick={() => refetchAuthState()}>
            Retry
          </Button>
        </>
      )}
      <Button
        isFullWidth
        size="large"
        isDisabled={resolveUser.isPending || isInviteInvalid || isInviteResolving || !authState}
        onClick={handleEmailSubmit}
      >
        Continue
      </Button>
      {!isInviteInvalid && (
        <SocialLoginButtons providers={oauthProviders} next={next} variant="signup" inviteToken={inviteToken} />
      )}
      <div className={registerStyles.altAction}>
        {isInviteInvalid ? (
          <RouterLink to={registerWithoutInviteHref}>Sign up without an invite</RouterLink>
        ) : (
          <>Already have an account? <RouterLink to={loginHref}>Sign in</RouterLink></>
        )}
      </div>
    </form>
  );

  const renderPasswordStep = () => (
    <form
      className={styles.card}
      onSubmit={event => {
        event.preventDefault();
        handleRegister();
      }}
    >
      <h1 className={styles.heading}>{heading}</h1>
      <div className={styles.fieldGroup}>
        <LabeledTextInput label="Email" name="email" value={email} isReadOnly />
        <LabeledPasswordInput ref={passwordRef} label="Password" value={password} onChange={setPassword} isRequired />
        <LabeledPasswordInput
          label="Confirm password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          isRequired
        />
      </div>
      {isPasswordMismatch && (
        <p role="alert" className={styles.error}>
          Passwords do not match.
        </p>
      )}
      <div className={registerStyles.meter}>
        <PasswordMeter strength={passwordStrength.label} />
      </div>
      <div className={registerStyles.rules}>
        <PasswordValidationTooltip strength={passwordStrength} />
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {/* A submit button lets Enter submit the form; the signup runs in the form's onSubmit, so onClick is unused. */}
      <Button isFullWidth size="large" isSubmitButton isDisabled={!canSubmit || register.isPending} onClick={() => undefined}>
        Sign up
      </Button>
      <Button
        type="anchor"
        size="small"
        icon={icons.arrowLeft}
        className={styles.backLink}
        isDisabled={register.isPending}
        onClick={handleReset}
      >
        Use a different email
      </Button>
      <div className={registerStyles.terms}>
        <AuthFormTermsLink />
      </div>
    </form>
  );

  return (
    <AuthLayoutTwoCol>
      {step === 'email'
        && (isSignupDisabled && !inviteToken ? renderSignupDisabled() : renderEmailStep())}
      {step === 'org-picker' && (
        <OrgPickerStep
          candidates={resolveUser.data?.candidates ?? []}
          signupSuggestions={resolveUser.data?.signupSuggestions ?? []}
          next={next}
          onPickCandidate={() => router.push(loginHref)}
          onCreateNew={handleCreateNew}
          onBack={() => setStep('email')}
        />
      )}
      {step === 'confirm-password' && (
        <ConfirmPasswordStep
          email={email}
          isSubmitting={register.isPending}
          error={error}
          onSubmit={submitRegister}
          onBack={() => {
            isRegisterAwaitedRef.current = false;
            setStep('org-picker');
          }}
        />
      )}
      {step === 'password' && renderPasswordStep()}
      {step === 'verify' && (
        <VerifyEmailStep
          email={email}
          onVerified={result => hardRedirectTo(resolveVerifiedTarget(result))}
        />
      )}
      {step === 'mfa' && mfaMethod && (
        <MfaStep mfaMethod={mfaMethod} next={next} onBack={handleReset} />
      )}
    </AuthLayoutTwoCol>
  );
};

export default Register;
